import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQueryClient } from '@tanstack/react-query';
import type {
  FilterChangedEvent,
  FilterModel,
  GridApi,
  GridReadyEvent,
  IServerSideDatasource,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import {
  ChevronDownIcon,
  PackagePlusIcon,
  PencilIcon,
  PlusIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  createGridTotalStore,
  GridStatusBar,
  WithLocalStorage,
  WorkflowTabBar,
} from '@/components';
import useDebounce from '@/components/hooks/useDebounce';
import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  useComboboxAnchor,
} from '@/components/ui/combobox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef, sideBar } from '@/grid/defaults';
import { cn } from '@/lib/utils';
import { PrimaryContext } from '@/services';
import {
  addAnimalsToShipment,
  ANIMAL_FIELD_MAPPINGS,
  ANIMAL_WRITE_ACTIONS,
  type AnimalRow,
  animalsCountOptions,
  type AnimalTypeLabel,
  createAnimalsOptions,
  createProceduresOptions,
  type ProcedureRow,
  proceduresCountOptions,
} from '@/services/animal-management';
import { useContextMenu } from '@/utils/context-menu';
import { addGridQueryParams } from '@/utils/query';

import {
  applyDefaultSetFilterValues,
  useAnimalColumns,
  useProcedureColumns,
} from './columns';
import { AddAnimalDialog } from './components/AddAnimalDialog';
import { AddProceduresDialog } from './components/AddProceduresDialog';
import { AddToShipmentDialog } from './components/AddToShipmentDialog';
import { BulkEditAnimalsDialog } from './components/BulkEditAnimalsDialog';
import { EditAnimalDialog } from './components/EditAnimalDialog';
import {
  type ActiveAnimalGrid,
  AnimalManagementProvider,
  useAnimalManagementContext,
} from './context';

const toBaseParams = (search: string, types: AnimalTypeLabel[]) => {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (types.length > 0) params.set('quickFilters', types.join(','));
  return params;
};

const quickFilterOptions: AnimalTypeLabel[] = [
  'Calf',
  'LOPU Donor',
  'Donor',
  'Recipient',
];

function AnimalManagementContent() {
  const defaultGridOptions = useDefaultGridOptions();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { themeMode } = useContext(PrimaryContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    activeGrid,
    setActiveGrid,
    selectedAnimals,
    selectedProcedures,
    setSelectedAnimals,
    setSelectedProcedures,
  } = useAnimalManagementContext();
  // Each tab keeps its own search and type filter. The URL carries the
  // active tab's values.
  const [tabSearch, setTabSearch] = useState<Record<ActiveAnimalGrid, string>>(
    () => ({
      animals: '',
      procedures: '',
      [activeGrid]: searchParams.get('search') ?? '',
    }),
  );
  const [tabTypes, setTabTypes] = useState<
    Record<ActiveAnimalGrid, AnimalTypeLabel[]>
  >(() => ({
    animals: [],
    procedures: [],
    [activeGrid]: (searchParams.get('types')?.split(',') ?? []).filter(
      (type): type is AnimalTypeLabel =>
        quickFilterOptions.includes(type as AnimalTypeLabel),
    ),
  }));
  const search = tabSearch[activeGrid];
  const quickFilters = tabTypes[activeGrid];
  const setSearch = (value: string) =>
    setTabSearch((prev) => ({ ...prev, [activeGrid]: value }));
  const setQuickFilters = (value: AnimalTypeLabel[]) =>
    setTabTypes((prev) => ({ ...prev, [activeGrid]: value }));
  // Column filters/sort from a shared link. Applied after each grid restores
  // its localStorage layout so the link wins.
  const pendingGridUrlState = useRef<
    Record<
      ActiveAnimalGrid,
      { filters: string | null; sort: string | null } | null
    >
  >(
    (() => {
      const read = (grid: ActiveAnimalGrid) => {
        const filters = searchParams.get(`${grid}Filters`);
        const sort = searchParams.get(`${grid}Sort`);
        return filters || sort ? { filters, sort } : null;
      };
      return { animals: read('animals'), procedures: read('procedures') };
    })(),
  );
  const [linkHasAnimalGridState] = useState(
    () => searchParams.has('animalsFilters') || searchParams.has('animalsSort'),
  );
  const [addAnimal, setAddAnimal] = useState(false);
  const [addProceduresOpen, setAddProceduresOpen] = useState(false);
  const [bulkEditAnimalsOpen, setBulkEditAnimalsOpen] = useState(false);
  const [addToShipmentOpen, setAddToShipmentOpen] = useState(false);
  const animalColumns = useAnimalColumns();
  const procedureColumns = useProcedureColumns();

  const [editAnimal, setEditAnimal] = useState<AnimalRow | null>(null);

  const [animalsTotal] = useState(createGridTotalStore);
  const [proceduresTotal] = useState(createGridTotalStore);
  const animalsCountRequest = useRef(0);
  const proceduresCountRequest = useRef(0);

  const animalsStatusBar = useMemo(
    () => ({
      statusPanels: [
        {
          statusPanel: GridStatusBar,
          statusPanelParams: {
            entityLabel: 'animals',
            totalStore: animalsTotal,
          },
          align: 'left' as const,
        },
      ],
    }),
    [animalsTotal],
  );
  const proceduresStatusBar = useMemo(
    () => ({
      statusPanels: [
        {
          statusPanel: GridStatusBar,
          statusPanelParams: {
            entityLabel: 'procedures',
            totalStore: proceduresTotal,
          },
          align: 'left' as const,
        },
      ],
    }),
    [proceduresTotal],
  );

  const quickFilterAnchor = useComboboxAnchor();
  const animalsGridRef = useRef<AgGridReact<AnimalRow>>(null);
  const proceduresGridRef = useRef<AgGridReact<ProcedureRow>>(null);
  const { getContextMenuItems } = useContextMenu({ idKey: 'id' });

  // Search runs server-side against the active grid's endpoint, so wait for
  // typing to settle instead of purging the grid on every keystroke.
  const animalsSearch = useDebounce(tabSearch.animals.trim(), 400);
  const proceduresSearch = useDebounce(tabSearch.procedures.trim(), 400);
  const debouncedSearch =
    activeGrid === 'animals' ? animalsSearch : proceduresSearch;

  const animalsParams = useMemo(
    () => toBaseParams(animalsSearch, tabTypes.animals),
    [animalsSearch, tabTypes.animals],
  );
  const proceduresParams = useMemo(
    () => toBaseParams(proceduresSearch, tabTypes.procedures),
    [proceduresSearch, tabTypes.procedures],
  );

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) next.set('search', debouncedSearch);
        else next.delete('search');
        if (quickFilters.length) next.set('types', quickFilters.join(','));
        else next.delete('types');
        return next.toString() === prev.toString() ? prev : next;
      },
      { replace: true },
    );
  }, [debouncedSearch, quickFilters, setSearchParams]);

  const writeGridStateToUrl = useCallback(
    (grid: ActiveAnimalGrid, api: GridApi) => {
      if (pendingGridUrlState.current[grid]) return;
      const filterModel = api.getFilterModel();
      const sort = api
        .getColumnState()
        .filter((column) => column.sort)
        .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
        .map((column) => `${column.colId}:${column.sort}`)
        .join(',');
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (filterModel && Object.keys(filterModel).length)
            next.set(`${grid}Filters`, JSON.stringify(filterModel));
          else next.delete(`${grid}Filters`);
          if (sort) next.set(`${grid}Sort`, sort);
          else next.delete(`${grid}Sort`);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const loadGridUrlState = useCallback(
    (grid: ActiveAnimalGrid, api: GridApi) => {
      const state = pendingGridUrlState.current[grid];
      if (state) {
        let filterModel: FilterModel | null = null;
        try {
          filterModel = state.filters ? JSON.parse(state.filters) : null;
        } catch {
          // ignore a malformed link
        }
        api.applyColumnState({
          state: (state.sort ?? '')
            .split(',')
            .map((part) => part.split(':'))
            .filter(([, dir]) => dir === 'asc' || dir === 'desc')
            .map(([colId, dir], sortIndex) => ({
              colId,
              sort: dir as 'asc' | 'desc',
              sortIndex,
            })),
          defaultState: { sort: null },
        });
        api.setFilterModel(filterModel);
      }
      pendingGridUrlState.current[grid] = null;
      writeGridStateToUrl(grid, api);
    },
    [writeGridStateToUrl],
  );

  const onAnimalsLayoutLoaded = useCallback(
    (api: GridApi) => loadGridUrlState('animals', api),
    [loadGridUrlState],
  );
  const onProceduresLayoutLoaded = useCallback(
    (api: GridApi) => loadGridUrlState('procedures', api),
    [loadGridUrlState],
  );
  const onAnimalsGridStateChanged = useCallback(
    ({ api }: FilterChangedEvent | SortChangedEvent) =>
      writeGridStateToUrl('animals', api),
    [writeGridStateToUrl],
  );
  const onProceduresGridStateChanged = useCallback(
    ({ api }: FilterChangedEvent | SortChangedEvent) =>
      writeGridStateToUrl('procedures', api),
    [writeGridStateToUrl],
  );

  // Datasources read the current filters from a ref so they stay stable.
  // A new datasource object makes AG Grid refetch, even for the hidden tab.
  const animalsParamsRef = useRef(animalsParams);
  const proceduresParamsRef = useRef(proceduresParams);
  useEffect(() => {
    animalsParamsRef.current = animalsParams;
    proceduresParamsRef.current = proceduresParams;
  }, [animalsParams, proceduresParams]);

  // Each grid mounts the first time its tab is opened, so only the active
  // tab's endpoint is called.
  const [mountedGrids, setMountedGrids] = useState<Set<ActiveAnimalGrid>>(
    () => new Set([activeGrid]),
  );
  if (!mountedGrids.has(activeGrid)) {
    setMountedGrids(new Set([...mountedGrids, activeGrid]));
  }

  const animalsDataSource = useMemo<IServerSideDatasource>(
    () => ({
      getRows: (params) => {
        if (
          typeof params.request.startRow !== 'number' ||
          typeof params.request.endRow !== 'number'
        ) {
          return;
        }

        const searchParams = addGridQueryParams(
          animalsParamsRef.current,
          params.request.filterModel as FilterModel | null,
          params.request.sortModel,
          params.request.startRow,
          params.request.endRow,
          ANIMAL_FIELD_MAPPINGS,
        );

        if (params.request.startRow === 0) {
          const request = ++animalsCountRequest.current;
          animalsTotal.set(null);
          queryClient
            .fetchQuery(animalsCountOptions(searchParams))
            .then((total) => {
              if (request === animalsCountRequest.current)
                animalsTotal.set(total);
            })
            .catch(() => undefined);
        }

        queryClient
          .fetchQuery(createAnimalsOptions(searchParams))
          .then((response) => {
            const startRow = params.request.startRow ?? 0;
            params.success({
              rowData: response.items,
              rowCount: response.hasMore
                ? undefined
                : startRow + response.items.length,
            });
          })
          .catch(() => {
            params.fail();
          });
      },
    }),
    [queryClient, animalsTotal],
  );

  const proceduresDataSource = useMemo<IServerSideDatasource>(
    () => ({
      getRows: (params) => {
        if (
          typeof params.request.startRow !== 'number' ||
          typeof params.request.endRow !== 'number'
        ) {
          return;
        }

        const searchParams = addGridQueryParams(
          proceduresParamsRef.current,
          params.request.filterModel as FilterModel | null,
          params.request.sortModel,
          params.request.startRow,
          params.request.endRow,
        );

        if (params.request.startRow === 0) {
          const request = ++proceduresCountRequest.current;
          proceduresTotal.set(null);
          queryClient
            .fetchQuery(proceduresCountOptions(searchParams))
            .then((total) => {
              if (request === proceduresCountRequest.current)
                proceduresTotal.set(total);
            })
            .catch(() => undefined);
        }

        queryClient
          .fetchQuery(createProceduresOptions(searchParams))
          .then((response) => {
            const startRow = params.request.startRow ?? 0;
            params.success({
              rowData: response.items,
              rowCount: response.hasMore
                ? undefined
                : startRow + response.items.length,
            });
          })
          .catch(() => {
            params.fail();
          });
      },
    }),
    [queryClient, proceduresTotal],
  );

  const applySelection = useCallback(
    (api: GridApi | undefined, ids: string[]) => {
      if (!api) {
        return;
      }

      const gridSelectedIds = new Set(
        api.getSelectedRows().map((row) => String(row.id)),
      );
      const selectedIds = new Set(ids);
      const toDeselect = gridSelectedIds.difference(selectedIds);
      const toSelect = selectedIds.difference(gridSelectedIds);

      if (toDeselect.size === 0 && toSelect.size === 0) {
        return;
      }

      api.forEachNode((node) => {
        const id = node.data?.id;
        if (!id) {
          return;
        }
        if (toDeselect.has(String(id))) {
          node.setSelected(false);
          return;
        }
        if (toSelect.has(String(id))) {
          node.setSelected(true);
          return;
        }
      });
    },
    [],
  );

  const onAnimalsGridReady = useCallback(
    ({ api }: GridReadyEvent) => {
      // A shared link carries its own filters, so skip the defaults.
      if (linkHasAnimalGridState) return;
      // timeout should not be necessary,
      // but without it the default filter values are not applied on initial load for some reason
      setTimeout(() => {
        applyDefaultSetFilterValues(api);
      }, 1500);
    },
    [linkHasAnimalGridState],
  );

  const syncAnimalsSelection = useCallback(() => {
    applySelection(
      animalsGridRef.current?.api,
      selectedAnimals.map((item) => item.id),
    );
  }, [applySelection, selectedAnimals]);

  const syncProceduresSelection = useCallback(() => {
    applySelection(
      proceduresGridRef.current?.api,
      selectedProcedures.map((item) => item.id),
    );
  }, [applySelection, selectedProcedures]);

  const onAnimalRowDoubleClicked = useCallback(
    (event: RowDoubleClickedEvent<AnimalRow>) => {
      if (event.data) {
        setEditAnimal(event.data);
      }
    },
    [],
  );

  const onAnimalsSelectionChanged = (
    event: SelectionChangedEvent<AnimalRow>,
  ) => {
    setSelectedAnimals(event.api.getSelectedRows() as AnimalRow[]);
  };

  const onProceduresSelectionChanged = (
    event: SelectionChangedEvent<ProcedureRow>,
  ) => {
    const selected = event.api.getSelectedRows() as ProcedureRow[];
    setSelectedProcedures(selected);
  };

  // A tab's search and type filter only change while it is active, so each
  // change refetches just that grid.
  const isFirstAnimalsParams = useRef(true);
  useEffect(() => {
    if (isFirstAnimalsParams.current) {
      isFirstAnimalsParams.current = false;
      return;
    }
    animalsGridRef.current?.api?.refreshServerSide({ purge: true });
  }, [animalsParams]);

  const isFirstProceduresParams = useRef(true);
  useEffect(() => {
    if (isFirstProceduresParams.current) {
      isFirstProceduresParams.current = false;
      return;
    }
    proceduresGridRef.current?.api?.refreshServerSide({ purge: true });
  }, [proceduresParams]);

  // Writes change the totals, so drop cached counts before the grids refetch.
  const resetCounts = () => {
    queryClient.removeQueries({
      queryKey: ['animal-management', 'animals-count'],
    });
    queryClient.removeQueries({
      queryKey: ['animal-management', 'procedures-count'],
    });
  };

  const refreshAnimals = () => {
    resetCounts();
    animalsGridRef.current?.api?.refreshServerSide({ purge: true });
  };

  useEffect(() => {
    if (activeGrid === 'animals') {
      syncAnimalsSelection();
      return;
    }

    syncProceduresSelection();
  }, [activeGrid, syncAnimalsSelection, syncProceduresSelection]);

  return (
    <div className="flex h-full flex-col gap-3 bg-white p-2">
      <div className="flex flex-col gap-2">
        <div className="grid w-full grid-cols-2 gap-2 md:flex md:flex-wrap md:justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  size="lg"
                  className="w-full md:w-auto"
                  disabled={!ANIMAL_WRITE_ACTIONS.addAnimal}
                >
                  <PlusIcon className="mr-1.5 size-4" />
                  {t('animalManagement.addAnimal', 'Add Animal')}
                  <ChevronDownIcon className="ml-1.5 size-4" />
                </Button>
              }
            />
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setAddAnimal(true)}>
                <PlusIcon className="mr-2 size-4" />
                {t('common.add', 'Add')}
              </DropdownMenuItem>
              <DropdownMenuItem disabled>
                <PlusIcon className="mr-2 size-4" />
                {t('common.bulkAdd', 'Bulk Add')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  size="lg"
                  className="w-full md:w-auto"
                  disabled={!selectedAnimals.length}
                >
                  <PencilIcon className="mr-1.5 size-4" />
                  {t('animalManagement.bulkEdit.editAnimal', 'Edit Animal')}
                  <ChevronDownIcon className="ml-1.5 size-4" />
                </Button>
              }
            />
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={selectedAnimals.length !== 1}
                onClick={() => setEditAnimal(selectedAnimals[0])}
              >
                <PencilIcon className="mr-2 size-4" />
                {t('common.edit', 'Edit')}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={
                  !ANIMAL_WRITE_ACTIONS.bulkEdit || selectedAnimals.length <= 1
                }
                onClick={() => setBulkEditAnimalsOpen(true)}
              >
                <PencilIcon className="mr-2 size-4" />
                {t('common.bulkEdit', 'Bulk Edit')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            size="lg"
            className="w-full md:w-auto"
            disabled={
              !ANIMAL_WRITE_ACTIONS.addProcedures || !selectedAnimals.length
            }
            onClick={() => setAddProceduresOpen(true)}
          >
            <PlusIcon className="mr-1.5 size-4" />
            {t('animalManagement.addProcedures.title', 'Add Procedures')}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  size="lg"
                  className="w-full md:w-auto"
                  disabled={
                    !ANIMAL_WRITE_ACTIONS.editProcedure ||
                    !selectedProcedures.length
                  }
                >
                  <PencilIcon className="mr-1.5 size-4" />
                  {t('animalManagement.editProcedure', 'Edit Procedure')}
                  <ChevronDownIcon className="ml-1.5 size-4" />
                </Button>
              }
            />
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled={selectedProcedures.length !== 1}>
                <PencilIcon className="mr-2 size-4" />
                {t('common.edit', 'Edit')}
              </DropdownMenuItem>
              <DropdownMenuItem disabled={selectedProcedures.length <= 1}>
                <PencilIcon className="mr-2 size-4" />
                {t('common.bulkEdit', 'Bulk Edit')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            size="lg"
            className="col-span-2 w-full md:w-auto"
            disabled={
              !ANIMAL_WRITE_ACTIONS.addToShipment || !selectedAnimals.length
            }
            onClick={() => setAddToShipmentOpen(true)}
          >
            <PackagePlusIcon className="mr-1.5 size-4" />
            {t('animalManagement.addToShipment.action', 'Add to Shipment')}
          </Button>
        </div>
        <div className="flex flex-col gap-2 border-b border-gray-200 md:flex-row md:items-end">
          <WorkflowTabBar<ActiveAnimalGrid>
            tabs={[
              {
                key: 'animals',
                label: t('animalManagement.animals', 'Animals'),
                icon: 'pi pi-tags',
              },
              {
                key: 'procedures',
                label: t('animalManagement.procedures', 'Procedures'),
                icon: 'pi pi-clipboard',
              },
            ]}
            activeTab={activeGrid}
            onTabChange={setActiveGrid}
            className="order-last -mb-px w-full md:order-none md:w-auto"
          />
          <div className="flex flex-col gap-2 md:ml-auto md:flex-row md:items-center md:pb-2">
            <div className="flex w-full items-center gap-2 text-sm md:w-auto">
              <Combobox
                items={quickFilterOptions}
                multiple
                openOnInputClick
                value={quickFilters}
                onValueChange={(next: AnimalTypeLabel[]) =>
                  setQuickFilters(next)
                }
              >
                <ComboboxChips
                  ref={quickFilterAnchor}
                  className="min-h-9 w-full cursor-pointer bg-white md:w-auto md:min-w-64"
                >
                  {quickFilters.map((option) => (
                    <ComboboxChip key={option} aria-label={option}>
                      {option}
                    </ComboboxChip>
                  ))}
                  <ComboboxChipsInput
                    aria-label={t(
                      'animalManagement.quickFilters',
                      'Quick Filters',
                    )}
                    className="cursor-pointer"
                    placeholder={
                      quickFilters.length === 0
                        ? t('animalManagement.allAnimalTypes', 'All types')
                        : ''
                    }
                  />
                  <ComboboxTrigger
                    aria-label={t(
                      'animalManagement.quickFilters',
                      'Quick Filters',
                    )}
                    className="ml-auto shrink-0 cursor-pointer"
                  />
                </ComboboxChips>
                <ComboboxContent anchor={quickFilterAnchor}>
                  <ComboboxList>
                    {(option: AnimalTypeLabel) => (
                      <ComboboxItem key={option} value={option}>
                        {option}
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </div>

            <div className="w-full md:w-64">
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-9 w-full"
                placeholder={
                  activeGrid === 'animals'
                    ? t('animalManagement.searchAnimals', 'Search animals...')
                    : t(
                        'animalManagement.searchProcedures',
                        'Search procedures...',
                      )
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className={cn(
          'min-h-0 flex-1',
          activeGrid === 'animals' ? 'block' : 'hidden',
        )}
      >
        {mountedGrids.has('animals') && (
          <WithLocalStorage
            storageKey="animal-management-animals-grid"
            onLoad={onAnimalsLayoutLoaded}
          >
            <AgGridReact
              gridOptions={defaultGridOptions}
              ref={animalsGridRef}
              className={cn(
                'h-full w-full',
                themeMode === 'dark'
                  ? 'ag-theme-transova-dark'
                  : 'ag-theme-transova',
              )}
              columnDefs={animalColumns}
              defaultColDef={defaultColDef}
              rowModelType="serverSide"
              serverSideDatasource={animalsDataSource}
              statusBar={animalsStatusBar}
              rowSelection="multiple"
              getRowId={(params) => params.data.id}
              getContextMenuItems={getContextMenuItems}
              sideBar={sideBar}
              onSelectionChanged={onAnimalsSelectionChanged}
              onRowDoubleClicked={onAnimalRowDoubleClicked}
              onGridReady={onAnimalsGridReady}
              onFilterChanged={onAnimalsGridStateChanged}
              onSortChanged={onAnimalsGridStateChanged}
              onFirstDataRendered={syncAnimalsSelection}
            />
          </WithLocalStorage>
        )}
      </div>

      <div
        className={cn(
          'min-h-0 flex-1',
          activeGrid === 'procedures' ? 'block' : 'hidden',
        )}
      >
        {mountedGrids.has('procedures') && (
          <WithLocalStorage
            storageKey="animal-management-procedures-grid"
            onLoad={onProceduresLayoutLoaded}
          >
            <AgGridReact
              gridOptions={defaultGridOptions}
              ref={proceduresGridRef}
              className={cn(
                'h-full w-full',
                themeMode === 'dark'
                  ? 'ag-theme-transova-dark'
                  : 'ag-theme-transova',
              )}
              columnDefs={procedureColumns}
              defaultColDef={defaultColDef}
              rowModelType="serverSide"
              serverSideDatasource={proceduresDataSource}
              statusBar={proceduresStatusBar}
              rowSelection="multiple"
              getRowId={(params) => params.data.id}
              getContextMenuItems={getContextMenuItems}
              sideBar={sideBar}
              onSelectionChanged={onProceduresSelectionChanged}
              onFilterChanged={onProceduresGridStateChanged}
              onSortChanged={onProceduresGridStateChanged}
              onFirstDataRendered={syncProceduresSelection}
            />
          </WithLocalStorage>
        )}
      </div>

      <EditAnimalDialog
        animal={editAnimal}
        onOpenChange={(open) => {
          if (!open) {
            setEditAnimal(null);
          }
        }}
        onSuccess={refreshAnimals}
      />
      <AddAnimalDialog
        open={addAnimal}
        onOpenChange={setAddAnimal}
        onSuccess={refreshAnimals}
        onOpenExisting={setEditAnimal}
      />
      <BulkEditAnimalsDialog
        open={bulkEditAnimalsOpen}
        selectedAnimals={selectedAnimals}
        onOpenChange={setBulkEditAnimalsOpen}
        onSuccess={refreshAnimals}
      />
      <AddProceduresDialog
        open={addProceduresOpen}
        selectedAnimals={selectedAnimals}
        onOpenChange={setAddProceduresOpen}
        onSuccess={() => {
          resetCounts();
          proceduresGridRef.current?.api?.refreshServerSide({ purge: true });
        }}
      />
      <AddToShipmentDialog
        open={addToShipmentOpen}
        selectedAnimals={selectedAnimals}
        onOpenChange={setAddToShipmentOpen}
        onSubmit={(values) => {
          const result = addAnimalsToShipment(values);
          console.warn('added animals to shipment', result);
        }}
      />
    </div>
  );
}

export function AnimalManagementPage() {
  useEffect(() => {
    document.documentElement.classList.add('preflight');
    return () => document.documentElement.classList.remove('preflight');
  }, []);

  return (
    <AnimalManagementProvider>
      <AnimalManagementContent />
    </AnimalManagementProvider>
  );
}
